import clsx from 'clsx';
import { CSSProperties, useMemo, useRef } from 'react';
import { parse } from 'ansicolor';
import stripAnsi from 'strip-ansi';
import toStyle from 'css-to-style';

import { highlightKeyword } from '@@/LogViewer/helpers/highlightKeyword';
import {
  KeywordIndexInterface,
  LogInterface,
  LogSpanInterface,
} from '@@/LogViewer/types';
import { useLogViewerContext } from '@@/LogViewer/context';

import { LogSpan } from './LogSpan';
import styles from './LogRow.module.css';

interface Props {
  lineIndex: number;
}

export function LogRow({ lineIndex }: Props) {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const {
    logs,
    searchStatus: { focusedKeywordIndex },
    controllerState: { wrapLine, keyword, showLineNumbers },
  } = useLogViewerContext();

  const log = logs.logs[lineIndex];
  const focusedKeywordIndexInLine = getFocusedKeywordIndexInLine(
    focusedKeywordIndex,
    logs.keywordIndexes,
    log
  );

  // format log spans on-demand for better performance
  const formattedLog = useMemo(() => {
    if (log.spans && log.spans.length > 0 && log.spans[0].text !== '') {
      return log;
    }
    return {
      ...log,
      spans: formatLogLine(log.line),
    };
  }, [log]);

  const spans = useMemo(
    () => highlightKeyword(formattedLog, keyword, focusedKeywordIndexInLine),
    [formattedLog, keyword, focusedKeywordIndexInLine]
  );

  // useSizeCache - computeRowHeight relies on this dom structure, ensure useSizeCache is updated if there are any changes to elements / styles here
  return (
    <pre
      className={clsx(styles.logRow, {
        'whitespace-pre-wrap': wrapLine,
        'whitespace-pre': !wrapLine,
      })}
    >
      {showLineNumbers && (
        <div className={styles.lineNumber}>{log.lineNumber}</div>
      )}
      <div ref={rowRef}>{spans.map(LogSpan)}</div>
    </pre>
  );
}

// Find out the keyword index in line of the focused keyword
// Return -1 if the focused keyword is not on current line
function getFocusedKeywordIndexInLine(
  focusedKeywordIndex: number,
  keywordIndexes: KeywordIndexInterface[],
  log: LogInterface
) {
  let focusedKeywordIndexInLine = -1;

  if (focusedKeywordIndex >= 0 && keywordIndexes[focusedKeywordIndex]) {
    if (keywordIndexes[focusedKeywordIndex].lineNumber + 1 === log.lineNumber) {
      focusedKeywordIndexInLine =
        keywordIndexes[focusedKeywordIndex].indexInLine;
    }
  }

  return focusedKeywordIndexInLine;
}

function formatLogLine(line: string): LogSpanInterface[] {
  try {
    const { spans } = parse(line);
    // Use array map for better performance and linting compliance
    const formattedSpans: LogSpanInterface[] = spans.map((span) => {
      const style = toStyle(span.css) as CSSProperties;
      const text = stripAnsi(span.text);
      return { text, style };
    });

    return formattedSpans.length > 0
      ? formattedSpans
      : [{ text: line, style: {} }];
  } catch {
    // Fallback for malformed ANSI
    return [{ text: line, style: {} }];
  }
}
